import type { RecordingId } from "@repo/app-types/entities/recording";
import type {
  CallOutlineSegment,
  TeammateAccountCallOutline,
} from "@repo/app-types/entities/teammateAccountCallOutline";
import type { AccountGradingRecording } from "@repo/app-types/entities/teammatePerformanceSummary";
import { DefaultSkeleton } from "@repo/ui/Skeletons/skeletons";
import { IconChevronDown, IconChevronUp } from "@tabler/icons-react";
import { DateTime, Duration } from "luxon";
import { type FC, useEffect, useRef, useState } from "react";
import { Button as AriaButton } from "react-aria-components";
import { twMerge } from "tailwind-merge";
import type { CallPosition } from "./teammateAccountCallOutlineNavigation.js";

export interface CallNavigatorProps {
  recordings: readonly AccountGradingRecording[];
  callOutline: TeammateAccountCallOutline | undefined;
  isOutlineLoading?: boolean;
  isOutlineError?: boolean;
  /** The recording selected in the transcript audio player. */
  expandedRecordingId: RecordingId | undefined;
  /** The key moment under the audio playhead. */
  audioPosition?: CallPosition;
  /** The key moment nearest the transcript's reading position. */
  scrollPosition?: CallPosition;
  onCallRequest: (recordingId: RecordingId) => void;
  onSegmentRequest: (position: CallPosition) => void;
}

export interface CallNavigationRowProps {
  callNumber: number;
  recordedAt: string;
  title: string;
  isExpanded: boolean;
  isStuck?: boolean;
  chaptersId?: string;
  onPress: () => void;
}

export const CallNavigationRow: FC<CallNavigationRowProps> = (props) => {
  const {
    callNumber,
    recordedAt,
    title,
    isExpanded,
    isStuck,
    chaptersId,
    onPress,
  } = props;

  return (
    <AriaButton
      data-call-header
      className={twMerge(
        "sticky top-0 z-10 flex w-full items-start justify-between gap-2 rounded-lg bg-card px-2.5 py-1.5 text-left hover:bg-gray-50 focus-visible:ring-2 focus-visible:ring-indigo-600 focus-visible:outline-none",
        isStuck &&
          "rounded-t-none after:pointer-events-none after:absolute after:inset-x-0 after:-bottom-3 after:h-3 after:bg-linear-to-b after:from-card after:to-transparent after:content-['']",
      )}
      aria-expanded={isExpanded}
      aria-controls={isExpanded ? chaptersId : undefined}
      onPress={onPress}
    >
      <span className="min-w-0">
        <span className="block text-2xs font-normal text-muted-foreground">
          Call {callNumber} &middot;{" "}
          {DateTime.fromISO(recordedAt).toLocaleString(DateTime.DATETIME_MED)}
        </span>
        <span className="mt-0.5 block text-xs font-semibold text-foreground">
          {title}
        </span>
      </span>
      {isExpanded ? (
        <IconChevronUp className="mt-3 size-4 shrink-0 text-muted-foreground" />
      ) : (
        <IconChevronDown className="mt-3 size-4 shrink-0 text-muted-foreground" />
      )}
    </AriaButton>
  );
};

export interface CallKeyMomentRowProps {
  segment: CallOutlineSegment;
  isAudioActive: boolean;
  isScrollCurrent: boolean;
  onPress: () => void;
}

export const CallKeyMomentRow: FC<CallKeyMomentRowProps> = (props) => {
  const { segment, isAudioActive, isScrollCurrent, onPress } = props;

  return (
    <AriaButton
      className={twMerge(
        "flex w-full items-start gap-3 rounded-lg px-2.5 py-2 text-left text-xs hover:bg-gray-50 focus-visible:ring-2 focus-visible:ring-indigo-600 focus-visible:outline-none",
        isScrollCurrent && "bg-gray-50",
        isAudioActive && "bg-blue-50 text-blue-900 hover:bg-blue-50",
      )}
      aria-current={isAudioActive ? "location" : undefined}
      onPress={onPress}
    >
      <span className="w-16 shrink-0 font-normal text-muted-foreground tabular-nums">
        {Duration.fromMillis(segment.start_timestamp).toFormat("h:mm:ss")}
      </span>
      <span className="min-w-0 font-medium">{segment.title}</span>
    </AriaButton>
  );
};

/** Navigates the calls and chapters available in one grading. */
export const CallNavigator: FC<CallNavigatorProps> = (props) => {
  const {
    recordings,
    callOutline,
    isOutlineLoading,
    isOutlineError,
    expandedRecordingId,
    audioPosition,
    scrollPosition,
    onCallRequest,
    onSegmentRequest,
  } = props;
  const outlinesByRecordingId = new Map(
    callOutline?.calls.map((call) => [call.recording_id, call]) ?? [],
  );
  const navRef = useRef<HTMLElement>(null);
  const [stuckCallId, setStuckCallId] = useState<RecordingId>();

  useEffect(() => {
    const activeCall = Array.from(
      navRef.current?.querySelectorAll<HTMLElement>(
        "[data-call-recording-id]",
      ) ?? [],
    ).find(
      (element) => element.dataset.callRecordingId === expandedRecordingId,
    );
    activeCall?.scrollIntoView({ block: "nearest" });
  }, [expandedRecordingId]);

  useEffect(() => {
    const nav = navRef.current;
    const scrollPane = nav?.closest<HTMLElement>(
      "[data-call-scroll-container]",
    );
    if (!nav || !scrollPane) {
      return;
    }

    const updateStuckCall = () => {
      const stickyTop =
        scrollPane.getBoundingClientRect().top +
        Number.parseFloat(getComputedStyle(scrollPane).paddingTop);
      const stuckSection = Array.from(
        nav.querySelectorAll<HTMLElement>("[data-call-recording-id]"),
      ).find((section) => {
        const bounds = section.getBoundingClientRect();
        const headerHeight =
          section.querySelector<HTMLElement>("[data-call-header]")
            ?.offsetHeight ?? 0;
        return (
          bounds.top < stickyTop - 1 && bounds.bottom > stickyTop + headerHeight
        );
      });
      const nextId = recordings.find(
        (recording) => recording.id === stuckSection?.dataset.callRecordingId,
      )?.id;
      setStuckCallId((currentId) =>
        currentId === nextId ? currentId : nextId,
      );
    };

    scrollPane.addEventListener("scroll", updateStuckCall, { passive: true });
    window.addEventListener("resize", updateStuckCall);
    updateStuckCall();
    return () => {
      scrollPane.removeEventListener("scroll", updateStuckCall);
      window.removeEventListener("resize", updateStuckCall);
    };
  }, [callOutline, expandedRecordingId, recordings]);

  return (
    <nav ref={navRef} aria-label="Calls">
      <ol className="space-y-2">
        {recordings.map((recording, index) => {
          const outline = outlinesByRecordingId.get(recording.id);
          const isExpanded = recording.id === expandedRecordingId;
          const segments = outline?.segments ?? [];
          const title =
            outline?.title?.trim() ||
            recording.title.trim() ||
            `Call ${index + 1}`;
          const chaptersId = `call-${recording.id}-chapters`;

          return (
            <li key={recording.id} data-call-recording-id={recording.id}>
              <CallNavigationRow
                callNumber={index + 1}
                recordedAt={recording.recorded_at}
                title={title}
                isExpanded={isExpanded}
                isStuck={stuckCallId === recording.id}
                chaptersId={chaptersId}
                onPress={() => {
                  if (!isExpanded) onCallRequest(recording.id);
                }}
              />

              {isExpanded ? (
                <div id={chaptersId}>
                  {segments.length > 0 ? (
                    <ol>
                      {segments.map((segment) => {
                        const isAudioActive =
                          audioPosition?.recordingId === recording.id &&
                          audioPosition.timestamp >= segment.start_timestamp &&
                          audioPosition.timestamp < segment.end_timestamp;
                        const isScrollCurrent =
                          scrollPosition?.recordingId === recording.id &&
                          scrollPosition.timestamp >= segment.start_timestamp &&
                          scrollPosition.timestamp < segment.end_timestamp;

                        return (
                          <li
                            key={`${segment.title}-${segment.start_timestamp}-${segment.end_timestamp}`}
                          >
                            <CallKeyMomentRow
                              segment={segment}
                              isAudioActive={isAudioActive}
                              isScrollCurrent={isScrollCurrent}
                              onPress={() =>
                                onSegmentRequest({
                                  recordingId: recording.id,
                                  timestamp: segment.start_timestamp,
                                })
                              }
                            />
                          </li>
                        );
                      })}
                    </ol>
                  ) : isOutlineLoading ? (
                    <div className="px-3 py-2">
                      <DefaultSkeleton />
                    </div>
                  ) : isOutlineError ? (
                    <p className="px-3 py-2 text-sm text-destructive">
                      Failed to load call chapters.
                    </p>
                  ) : null}
                </div>
              ) : null}
            </li>
          );
        })}
      </ol>
    </nav>
  );
};
